import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Typography,
  CircularProgress,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import SingleDayDialog from './single-day-dialog';
import { services } from '../pages/Sell/services';
import { useTrigger } from './useTrigger';

const AddedBullions = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [addedAssets, setAddedAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const { trigger } = useTrigger();

  const [currentDateStr, setCurrentDateStr] = useState('امروز');

  useEffect(() => {
    fetchBullionsForSpecificDay(new Date(), true);
  }, [trigger]);

  const fetchBullionsForSpecificDay = async (date, isToday = false) => {
    try {
      if (!isToday) {
        setLoading(true);
      }
      setIsDialogOpen(false);

      const dateIso = date.toISOString();

      const data = await services.getTreasuryChargeStatus({
        date: dateIso,
      });

      setAddedAssets(data || []);

      if (isToday) {
        setCurrentDateStr('امروز');
      } else {
        setCurrentDateStr(
          new Intl.DateTimeFormat('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }).format(date)
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!isToday) {
        setLoading(false);
      }
    }
  };

  const groupedAssets = groupByArray(addedAssets, 'branch');

  let total1g = 0,
    total2g = 0,
    total5g = 0,
    total10g = 0,
    total20g = 0,
    total50g = 0,
    total100g = 0;
  let accumulatedAllBranchsGram = 0;

  groupedAssets.forEach(asset => {
    total1g += Number(asset?.values?.BULLION_1 ?? 0);
    total2g += Number(asset?.values?.BULLION_2 ?? 0);
    total5g += Number(asset?.values?.BULLION_5 ?? 0);
    total10g += Number(asset?.values?.BULLION_10 ?? 0);
    total20g += Number(asset?.values?.BULLION_20 ?? 0);
    total50g += Number(asset?.values?.BULLION_50 ?? 0);
    total100g += Number(asset?.values?.BULLION_100 ?? 0);
    accumulatedAllBranchsGram += groupByBullion(asset.values);
  });

  return (
    <Grid
      pt={'0px !important'}
      item
      xs={12}
      //   md={6}
      sx={{ mb: { xs: 2, sm: 2, md: 0 } }}
    >
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: { xs: '12px', sm: '14px', md: '16px' },
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(8px) saturate(180%)',
          p: { xs: 2, sm: 2.5, md: 3 },
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: { xs: 2, sm: 2, md: 3 } }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant={{ xs: 'h6', sm: 'h6', md: 'h5' }}
              sx={{
                fontWeight: 700,
                color: '#111827',
                letterSpacing: '0.5px',
              }}
            >
              شمش‌های ورودی به خزانه
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1, color: '#10b981', fontWeight: 'bold' }}
              >
                ({currentDateStr})
              </Typography>
            </Typography>
            {loading && (
              <CircularProgress size={20} sx={{ color: '#10b981' }} />
            )}
          </Stack>

          <Button
            variant="contained"
            size="small"
            onClick={() => setIsDialogOpen(true)}
            disabled={loading}
            sx={{
              backgroundColor: '#10b981',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              '&:hover': { backgroundColor: '#059669' },
            }}
          >
            ورودی در تاریخ خاص
          </Button>
        </Stack>

        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(3px)',
                zIndex: 10,
                borderRadius: '12px',
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <CircularProgress size={32} sx={{ color: '#10b981' }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#059669' }}
                >
                  در حال دریافت...
                </Typography>
              </Stack>
            </Box>
          )}

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: 'none',
              background: 'transparent',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              minWidth: 'auto',
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.3s ease',
            }}
          >
            <Table
              aria-label="added bullions table"
              sx={{ tableLayout: 'auto' }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    background: 'linear-gradient(45deg, #10b981, #059669)',
                    borderRadius: '12px 12px 0 0',
                    '& th': {
                      fontWeight: 700,
                      color: '#fff',
                      py: { xs: 1.5, sm: 1.5, md: 2 },
                      px: { xs: 1, sm: 1.5, md: 2 },
                      fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                      borderBottom: 'none',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableCell sx={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                    شعبه
                  </TableCell>
                  <TableCell sx={{ width: '10%' }}>۱g</TableCell>
                  <TableCell sx={{ width: '10%' }}>۲g</TableCell>
                  <TableCell sx={{ width: '10%' }}>۵g</TableCell>
                  <TableCell sx={{ width: '10%' }}>۱۰g</TableCell>
                  <TableCell sx={{ width: '10%' }}>۲۰g</TableCell>
                  <TableCell sx={{ width: '10%' }}>۵۰g</TableCell>
                  <TableCell sx={{ width: '10%' }}>۱۰۰g</TableCell>
                  <TableCell sx={{ width: '10%' }}>مجموع</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedAssets.length === 0 && !loading && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ py: 3, color: '#6b7280' }}
                    >
                      در این تاریخ هیچ شمش ورودی ثبت نشده است.
                    </TableCell>
                  </TableRow>
                )}
                {groupedAssets.map((asset, index) => {
                  let accumulatedBranchGram = groupByBullion(asset.values);
                  return (
                    <TableRow
                      key={asset.branch}
                      sx={{
                        background:
                          index % 2 === 0
                            ? 'rgba(255, 255, 255, 0.9)'
                            : 'rgba(243, 244, 246, 0.9)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(209, 250, 229, 0.7)',
                          boxShadow: '0 4px 16px rgba(16, 185, 129, 0.2)',
                        },
                        '& td': {
                          color: '#111827',
                          borderBottom: 'none',
                          fontSize: {
                            xs: '0.7rem',
                            sm: '0.8rem',
                            md: '0.85rem',
                          },
                          py: { xs: 1, sm: 1.2, md: 1.5 },
                          px: { xs: 1, sm: 1.5, md: 2 },
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    >
                      <TableCell
                        sx={{ minWidth: '120px', whiteSpace: 'nowrap' }}
                      >
                        شعبه{' '}
                        {Number(asset.branch).toLocaleString('fa-IR', {
                          useGrouping: false,
                        })}
                      </TableCell>
                      <TableCell>
                        {Number(asset?.values?.BULLION_1 ?? 0).toLocaleString(
                          'fa-IR'
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(asset?.values?.BULLION_2 ?? 0).toLocaleString(
                          'fa-IR'
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(asset?.values?.BULLION_5 ?? 0).toLocaleString(
                          'fa-IR'
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(asset?.values?.BULLION_10 ?? 0).toLocaleString(
                          'fa-IR'
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(asset?.values?.BULLION_20 ?? 0).toLocaleString(
                          'fa-IR'
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(asset?.values?.BULLION_50 ?? 0).toLocaleString(
                          'fa-IR'
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(asset?.values?.BULLION_100 ?? 0).toLocaleString(
                          'fa-IR'
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(accumulatedBranchGram ?? 0).toLocaleString(
                          'fa-IR'
                        )}{' '}
                        گرم
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>

              {groupedAssets.length > 0 && (
                <TableFooter>
                  <TableRow
                    sx={{
                      background: 'rgba(16, 185, 129, 0.08)',
                      '& td': {
                        fontWeight: 800,
                        color: '#059669',
                        fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
                        py: 1.5,
                        px: { xs: 1, sm: 1.5, md: 2 },
                        borderTop: '2px solid rgba(16, 185, 129, 0.2)',
                        borderBottom: 'none',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <TableCell>مجموع کل</TableCell>
                    <TableCell>{total1g.toLocaleString('fa-IR')}</TableCell>
                    <TableCell>{total2g.toLocaleString('fa-IR')}</TableCell>
                    <TableCell>{total5g.toLocaleString('fa-IR')}</TableCell>
                    <TableCell>{total10g.toLocaleString('fa-IR')}</TableCell>
                    <TableCell>{total20g.toLocaleString('fa-IR')}</TableCell>
                    <TableCell>{total50g.toLocaleString('fa-IR')}</TableCell>
                    <TableCell>{total100g.toLocaleString('fa-IR')}</TableCell>
                    <TableCell>
                      {accumulatedAllBranchsGram.toLocaleString('fa-IR')} گرم
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* پاس دادن تنها یک تاریخ در تابع onConfirm */}
      <SingleDayDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={date => fetchBullionsForSpecificDay(date, false)}
      />
    </Grid>
  );
};

export default AddedBullions;

// توابع کمکی
function groupByArray(xs, key) {
  if (!xs || !Array.isArray(xs)) return [];
  return xs.reduce((rv, x) => {
    const v = key instanceof Function ? key(x) : x[key];
    const el = rv?.find(r => r && r[key] === v);
    if (el) {
      el.values[x.type] = x.amount;
    } else {
      rv.push({ [key]: v, values: { [x.type]: x.amount } });
    }
    return rv;
  }, []);
}

function groupByBullion(values) {
  let totalWeight = 0;
  for (let key in values) {
    totalWeight += values[key] * Number(key.replace('BULLION_', ''));
  }
  return totalWeight;
}
